/**
 * Known size convention per retailer for women's tops.
 *
 * Used as a fallback when garment_data.size_chart hasn't been scraped yet
 * for a tier retailer. Once real chart data exists, it takes precedence.
 *
 * Maintenance: add retailers as observed in production traffic. IDs match
 * canonical retailer IDs from retailerNormalisation.ts.
 */

export type SizeConvention = "letter" | "numeric" | "mixed";

export const RETAILER_SIZE_CONVENTION: Record<string, SizeConvention> = {
  // Letter-only retailers
  cos: "letter",
  house_of_fraser: "letter",
  lands_end: "letter",
  asos: "letter", // ASOS Design dominates; some sub-brands vary
  reiss: "letter",
  river_island: "letter",
  hm: "letter",
  new_look: "letter",
  zara: "letter",
  uniqlo: "letter",
  mango: "letter",
  bershka: "letter",
  pull_and_bear: "letter",
  and_other_stories: "letter",

  // Numeric-only retailers
  ms: "numeric", // M&S
  john_lewis: "numeric",

  // Mixed convention retailers
  next: "mixed",
  selfridges: "mixed", // multi-brand
  harvey_nichols: "mixed", // multi-brand
};

/**
 * Returns the known convention for a retailer, or null if unknown.
 */
export function getKnownConvention(
  retailerId: string | null | undefined,
): SizeConvention | null {
  if (!retailerId) return null;
  return RETAILER_SIZE_CONVENTION[retailerId] ?? null;
}

/**
 * Minimal hostname → retailer-id mapping for use in browser code. Mirrors
 * a subset of supabase/functions/_shared/retailerNormalisation.ts (which is
 * Deno-only). Add hosts here as needed for `detectTierConventions` lookups.
 */
const HOST_TO_RETAILER_ID: Record<string, string> = {
  "cos.com": "cos",
  "marksandspencer.com": "ms",
  "johnlewis.com": "john_lewis",
  "asos.com": "asos",
  "reiss.com": "reiss",
  "riverisland.com": "river_island",
  "next.co.uk": "next",
  "next.com": "next",
  "selfridges.com": "selfridges",
  "harveynichols.com": "harvey_nichols",
  "houseoffraser.co.uk": "house_of_fraser",
  "landsend.com": "lands_end",
  "landsend.co.uk": "lands_end",
  "newlook.com": "new_look",
  "zara.com": "zara",
  "uniqlo.com": "uniqlo",
  "shop.mango.com": "mango",
  "mango.com": "mango",
  "bershka.com": "bershka",
  "pullandbear.com": "pull_and_bear",
  "stories.com": "and_other_stories",
  "hm.com": "hm",
  "www2.hm.com": "hm",

  // Phase C additions — existing in retailerNormalisation but missing here
  "abercrombie.com": "abercrombie",
  "boohoo.com": "boohoo",
  "massimodutti.com": "massimo_dutti",
  "matalan.co.uk": "matalan",
  "prettylittlething.com": "plt",
  "prettylittlething.us": "plt",
  "urbanoutfitters.com": "urban_outfitters",

  // Phase C additions — new retailers
  "anthropologie.com": "anthropologie",
  "anthropologie.co.uk": "anthropologie",
  "aritzia.com": "aritzia",
  "bananarepublic.com": "banana_republic",
  "bananarepublic.gap.com": "banana_republic",
  "bananarepublic.co.uk": "banana_republic",
  "debenhams.com": "debenhams",
  "dorothyperkins.com": "dorothy_perkins",
  "everlane.com": "everlane",
  "fashionnova.com": "fashion_nova",
  "freepeople.com": "free_people",
  "frenchconnection.com": "french_connection",
  "frenchconnection.co.uk": "french_connection",
  "gap.com": "gap",
  "gap.co.uk": "gap",
  "gap.eu": "gap",
  "hobbs.com": "hobbs",
  "inthestyle.com": "in_the_style",
  "jcrew.com": "j_crew",
  "jcrew.co.uk": "j_crew",
  "jigsaw-online.com": "jigsaw",
  "karenmillen.com": "karen_millen",
  "lkbennett.com": "lk_bennett",
  "lulus.com": "lulus",
  "madewell.com": "madewell",
  "missguided.com": "missguided",
  "missguided.co.uk": "missguided",
  "monsoon.co.uk": "monsoon",
  "nastygal.com": "nasty_gal",
  "peacocks.co.uk": "peacocks",
  "primark.com": "primark",
  "princesspolly.com": "princess_polly",
  "princesspolly.co.uk": "princess_polly",
  "reformation.com": "reformation",
  "revolve.com": "revolve",
  "romwe.com": "romwe",
  "shein.com": "shein",
  "us.shein.com": "shein",
  "uk.shein.com": "shein",
  "wallis.co.uk": "wallis",
  "whistles.com": "whistles",
  "zaful.com": "zaful",

  // Phase C Part 3.2 — 5 new high-traffic retailers
  "hollandcooper.com": "holland_cooper",
  "meandem.com": "me_em",
  "coach.com": "coach",
  "uk.coach.com": "coach",
  "coachoutlet.com": "coach",
  "michaelkors.com": "michael_kors",
  "michaelkors.co.uk": "michael_kors",
  "mulberry.com": "mulberry",
};

export function retailerIdFromLink(link: string | null | undefined): string | null {
  if (!link) return null;
  try {
    const host = new URL(link).hostname.toLowerCase().replace(/^www\./, "");
    if (HOST_TO_RETAILER_ID[host]) return HOST_TO_RETAILER_ID[host];
    for (const [k, id] of Object.entries(HOST_TO_RETAILER_ID)) {
      if (host.endsWith(`.${k}`)) return id;
    }
  } catch {
    /* invalid URL */
  }
  return null;
}
